// src/components/Header.jsx
import styled from 'styled-components';
import { Link, NavLink } from 'react-router-dom';
import { CONTACTS } from '../config/contacts.js';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ACCENT = '#ff6600';
const LANG_ORDER = ['ua', 'en', 'de', 'pl', 'tr'];
const HIDDEN_LANGS = ['ru'];
const FLAGS = { ua: '🇺🇦', en: '🇬🇧', de: '🇩🇪', pl: '🇵🇱', tr: '🇹🇷', ru: '🇷🇺' };

const Bar = styled.header`
  position: sticky; top: 0; z-index: 1000;
  background: ${({theme})=> theme.mode==='light' ? 'rgba(248,249,252,.9)' : 'rgba(15,16,18,.72)'};
  backdrop-filter: saturate(160%) blur(10px);
  border-bottom: 1px solid ${({theme})=>theme.border};
`;

const Container = styled.div`
  width: ${({theme})=>theme.container}; margin: 0 auto;
`;

const TopBar = styled.div`
  display:flex; justify-content:center; align-items:center;
  min-height: 38px; border-bottom: 1px solid ${({theme})=>theme.border};
  padding: 6px 8px; gap: 12px; flex-wrap: wrap;
  background: ${({theme})=> theme.mode==='light' ? 'rgba(245,247,250,.85)' : 'transparent'};
`;

const Contacts = styled.div`
  display:flex; flex-wrap:wrap; gap: 12px; align-items:center;

  a {
    color: ${({theme})=>theme.text};
    text-decoration: none;
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 14px; padding: 2px 0; /* без фона/рамок — не «кнопки» */
  }

  svg { flex: 0 0 auto; width: 16px; height: 16px; color: ${ACCENT}; }
`;

const Row = styled.div`
  display:grid; grid-template-columns: 1fr auto 1fr; align-items:center;
  min-height: 68px;
  @media (max-width: 980px){ grid-template-columns: auto 1fr auto; }
`;

const Logo = styled(Link)`
  justify-self: start;
  display:inline-flex; align-items:center; gap:10px; text-decoration:none;
  font-weight: 900; font-size: 20px; letter-spacing:.2px; color: ${({theme})=>theme.text};
  img{ width:24px; height:24px; display:block; }
`;

const NavWrap = styled.div`
  justify-self: center; position: relative;
  @media (max-width: 980px){ display:none; }
`;

const Nav = styled.nav`
  display:flex; gap: 2px; align-items:center;
  a{
    --pad: 10px 14px;
    position: relative;
    padding: var(--pad);
    color: ${({theme})=>theme.textMuted};
    text-decoration: none; border-radius: 10px;
    transition: color .2s ease, background-color .2s ease;
  }
  a:hover{ color: ${({theme})=>theme.text}; }

  /* Активный — «пилюля» и лёгкая подложка */
  a.active{
    color: ${({theme})=>theme.text};
    background: ${({theme})=>theme.surface};
    box-shadow: ${({theme})=>theme.shadow.sm};
    border: 1px solid ${({theme})=>theme.border};
  }

  /* Подчёркивание на hover через псевдоэлемент */
  a::after{
    content:'';
    position:absolute; left: 12px; right: 12px; bottom: 6px;
    height: 2px; border-radius: 1px;
    background: ${ACCENT};
    transform: scaleX(0); transform-origin: center;
    transition: transform .22s ease;
  }
  a:hover::after{ transform: scaleX(1); }
`;

const Dropdown = styled.div`
  position: relative; margin-left: 2px;

  .trigger{
    padding: 10px 14px; border-radius: 10px;
    color: ${({theme})=>theme.textMuted};
    display:inline-flex; align-items:center; gap: 6px; cursor:pointer;
    transition: color .2s ease, background-color .2s ease;
  }
  .trigger:hover{ color: ${({theme})=>theme.text}; }
  .trigger::after{
    content:''; width: 6px; height: 6px; border-right:2px solid currentColor; border-bottom:2px solid currentColor;
    transform: rotate(45deg); margin-left: 4px; opacity:.7;
  }

  ul{
    position: absolute; top: calc(100% + 10px); left: 0; padding: 8px;
    min-width: 260px; background: ${({theme})=>theme.surface};
    border: 1px solid ${({theme})=>theme.border};
    border-radius: ${({theme})=>theme.radius.lg}px;
    box-shadow: ${({theme})=>theme.shadow.lg};
    display: ${({$open})=> $open ? 'grid' : 'none'};
    grid-template-columns: 1fr; gap: 2px; z-index: 1001;
  }
  li{ list-style:none; }
  li a{
    display:block; padding: 10px 10px; border-radius: 10px;
    color: ${({theme})=>theme.text}; text-decoration:none;
  }
  li a:hover{
    background: ${({theme})=>theme.body};
  }

  @media (max-width: 980px){ display:none; }
`;

const Right = styled.div`
  justify-self:end; display:flex; align-items:center; gap: 8px; flex-wrap:wrap;

  .btn{
    display:inline-flex; align-items:center; justify-content:center; gap: 6px;
    padding: 8px 12px; border-radius: ${({theme})=>theme.radius.pill}px;
    background: ${({theme})=>theme.surface};
    border: 1px solid ${({theme})=>theme.border};
    color: ${({theme})=>theme.text}; cursor: pointer; font-weight: 600; font-size: 14px;
    box-shadow: ${({theme})=>theme.shadow.sm};
    transition: transform .2s ease, background .2s ease, box-shadow .2s ease;
  }
  .btn:hover{ transform: translateY(-1px); background: ${({theme})=>theme.muted}; }
  @media (max-width: 980px){ .desktop-only{ display:none; } }
`;

const CircleButton = styled.button`
  position: relative;
  width: 42px; height: 42px;
  border-radius: 50%; border: 1px solid ${({theme})=>theme.border};
  background: ${({theme})=>theme.surface};
  color: ${({theme})=>theme.text};
  display: inline-flex; align-items:center; justify-content:center;
  box-shadow: ${({theme})=>theme.shadow.sm};
  cursor: pointer;
  transition: transform .18s ease, background .18s ease, box-shadow .18s ease;

  &:hover{ transform: translateY(-1px); background: ${({theme})=>theme.muted}; }

  svg{ width: 20px; height: 20px; opacity: .9; }

  .flag{
    position: absolute;
    inset: 50% auto auto 50%;
    transform: translate(-50%, -50%);
    width: 22px; height: 22px;
    display: grid; place-items: center;
    font-size: 16px; line-height: 1;
    border-radius: 50%;
    box-shadow: 0 0 0 1px ${({theme})=>theme.surface}, 0 4px 10px rgba(0,0,0,.14);
    background: ${({theme})=>theme.body};
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

const Burger = styled.button`
  display:none;
  @media (max-width: 980px){ display:inline-flex; }
  padding: 10px 12px; border-radius: 12px;
  background: ${({theme})=>theme.surface}; border:1px solid ${({theme})=>theme.border};
  color: ${({theme})=>theme.text}; cursor: pointer;
`;

/* Мобильное меню */
const Drawer = styled.div`
  display: none;
  @media (max-width: 980px){
    display: ${({$open})=> $open ? 'block' : 'none'};
    position: fixed; inset: 56px 0 0 0; /* под шапкой */
    background: ${({theme})=> theme.mode==='light' ? 'rgba(255,255,255,.98)' : 'rgba(10,11,12,.985)'};
    backdrop-filter: blur(6px);
    border-top: 1px solid ${({theme})=>theme.border};
    z-index: 999;
    overflow: auto;
  }
`;
const DrawerList = styled.nav`
  display:grid; gap: 8px; padding: 16px;

  a, .trigger{
    display:block; padding: 12px 14px; border-radius: 12px;
    text-decoration:none; color: ${({theme})=>theme.text};
    background: ${({theme})=>theme.surface};
    border: 1px solid ${({theme})=>theme.border};
  }
  .group{ display:grid; gap:6px; padding-left: 10px; }
`;

export default function Header({ isDark=false, onCycleTheme=()=>{} }) {
  const { phones = [], email, telegram } = CONTACTS;
  const { t, i18n } = useTranslation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [systemsOpen, setSystemsOpen] = useState(false);

  const dropdownTimers = useRef({ info: null, systems: null });
  const navRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
        setInfoOpen(false);
        setSystemsOpen(false);
      }
    };
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, []);

  const openWithCancel = (key) => {
    clearTimeout(dropdownTimers.current[key]);
    if (key==='info') setInfoOpen(true); else setSystemsOpen(true);
  };
  const closeWithDelay = (key, delay=140) => {
    clearTimeout(dropdownTimers.current[key]);
    dropdownTimers.current[key] = setTimeout(()=>{
      if (key==='info') setInfoOpen(false); else setSystemsOpen(false);
    }, delay);
  };

  const syncLangInUrl = (code) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (code === 'ua') {
      url.searchParams.delete('lang');
    } else {
      url.searchParams.set('lang', code);
    }
    window.history.replaceState({}, '', url);
  };
  const cycleLang = () => {
    const cur = (i18n.resolvedLanguage || i18n.language || 'ua').slice(0,2);
    const currentIdx = LANG_ORDER.indexOf(cur);
    const next = LANG_ORDER[(currentIdx + 1) % LANG_ORDER.length] || 'ua';
    i18n.changeLanguage(next);
    syncLangInUrl(next);
  };
  const langCode = (i18n.resolvedLanguage || i18n.language || 'ua').slice(0,2);
  const safeLang = (LANG_ORDER.includes(langCode) || HIDDEN_LANGS.includes(langCode)) ? langCode : 'ua';
  useEffect(() => {
    syncLangInUrl(safeLang);
  }, [safeLang]);
  const langLabel = safeLang.toUpperCase();

  return (
    <Bar>
      <Container>
        {/* Верхняя тонкая контактная полоска */}
        <TopBar>
          <Contacts>
            {phones.slice(0,3).map((p, idx)=>(
              <a key={idx} href={`tel:${p.tel}`} aria-label={`Call ${p.label}`}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V21a1 1 0 01-1 1C10.07 22 2 13.93 2 3a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z"/>
                </svg>
                {p.label}
              </a>
            ))}
            {email && (
              <a href={`mailto:${email}`} aria-label="Email">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20 4H4a2 2 0 00-2 2v.4l10 6.25 10-6.25V6a2 2 0 00-2-2zm0 4.45L12.53 14a1 1 0 01-1.06 0L4 8.45V18a2 2 0 002 2h12a2 2 0 002-2z"/>
                </svg>
                {email}
              </a>
            )}
            {telegram && (
              <a href={telegram} target="_blank" rel="noopener noreferrer" aria-label="Telegram">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M21.94 2.34a1 1 0 00-1.06-.15L2.64 10.5a1 1 0 00.08 1.86l5.22 1.85 1.84 5.22a1 1 0 001.86.08l8.31-18.24a1 1 0 00-.01-.93zM9.83 16.34l-1.15-3.25 7.5-5.51-6.35 6.94-3.15-1.12 12.59-6.03L9.83 16.34z"/>
                </svg>
                Telegram
              </a>
            )}
          </Contacts>
        </TopBar>

        {/* Лого — центр-меню — экшены */}
        <Row ref={navRef}>
          <Logo to="/" aria-label="MOMV home">
            <img src="/logo.png" alt="" />
            {t('brand')}
          </Logo>

          {/* Десктоп-меню */}
          <NavWrap>
            <Nav>
              <NavLink to="/" end>{t('nav.home')}</NavLink>
              <NavLink to="/metal">{t('nav.metal')}</NavLink>
              <NavLink to="/mebli">{t('nav.mebli')}</NavLink>
              <NavLink to="/hpl">{t('nav.hpl')}</NavLink>

              <Dropdown
                $open={systemsOpen}
                onMouseEnter={()=>openWithCancel('systems')}
                onMouseLeave={()=>closeWithDelay('systems')}
              >
                <span className="trigger">{t('nav.systems')}</span>
                <ul>
                  <li><NavLink to="/systems#cctv">{t('systems.cctv')}</NavLink></li>
                  <li><NavLink to="/systems#acs">{t('systems.acs')}</NavLink></li>
                  <li><NavLink to="/systems#gates">{t('systems.gates')}</NavLink></li>
                  <li><NavLink to="/systems#electro">{t('systems.electro')}</NavLink></li>
                  <li><NavLink to="/systems#water">{t('systems.water')}</NavLink></li>
                  <li><NavLink to="/systems#k1k2">{t('systems.k1k2')}</NavLink></li>
                  <li><NavLink to="/systems#vent">{t('systems.vent')}</NavLink></li>
                  <li><NavLink to="/systems#core">{t('systems.core')}</NavLink></li>
                </ul>
              </Dropdown>

              <NavLink to="/delivery">{t('nav.delivery')}</NavLink>

              <Dropdown
                $open={infoOpen}
                onMouseEnter={()=>openWithCancel('info')}
                onMouseLeave={()=>closeWithDelay('info')}
              >
                <span className="trigger">{t('nav.info')}</span>
                <ul>
                  <li><NavLink to="/info/docs">{t('nav.docs')}</NavLink></li>
                  <li><NavLink to="/info/vacancies">{t('nav.vacancies')}</NavLink></li>
                  <li><NavLink to="/info/faq">{t('nav.faq')}</NavLink></li>
                  <li><NavLink to="/articles">{t('nav.articles')}</NavLink></li>
                  <li><NavLink to="/info/videos">{t('nav.videos')}</NavLink></li>
                </ul>
              </Dropdown>

              <NavLink to="/contacts">{t('nav.contacts')}</NavLink>
            </Nav>
          </NavWrap>

          {/* Справа */}
          <Right>
            <CircleButton className="desktop-only" onClick={onCycleTheme} aria-label="Toggle theme">
              {isDark ? '🌙' : '☀️'}
            </CircleButton>
            <CircleButton aria-label="Switch language" onClick={cycleLang}>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 2a10 10 0 1010 10A10 10 0 0012 2zm6.83 9H16.1a16.44 16.44 0 00-.76-4.1A8 8 0 0118.83 11zM12 4.06c.8.9 1.77 2.63 2.23 5H9.77c.46-2.37 1.43-4.1 2.23-5zM8.66 6.9A16.44 16.44 0 007.9 11H5.17A8 8 0 018.66 6.9zM5.17 13H7.9a16.44 16.44 0 00.76 4.1A8 8 0 015.17 13zm3.6 0h4.46c-.46 2.37-1.43 4.1-2.23 5-.8-.9-1.77-2.63-2.23-5zm6.57 0h2.22A8 8 0 0115.1 17.1a16.44 16.44 0 00-.76-4.1zm-1.2-2h-4.1a18.61 18.61 0 01.39-1.55h3.32c.17.5.3 1 .4 1.55zM12 19.94c-.8-.9-1.77-2.63-2.23-5h4.46c-.46 2.37-1.43 4.1-2.23 5z"/>
              </svg>
              <span className="flag" aria-hidden="true">{FLAGS[safeLang] ?? '🌐'}</span>
              <span className="sr-only">{langLabel}</span>
            </CircleButton>

            {/* Бургер для мобилы */}
            <Burger aria-label="Toggle menu" aria-expanded={menuOpen} onClick={()=>setMenuOpen(o=>!o)}>☰</Burger>
          </Right>
        </Row>

        {/* Мобильное меню */}
        <Drawer $open={menuOpen}>
          <DrawerList onClick={()=>setMenuOpen(false)}>
            <NavLink to="/" end>{t('nav.home')}</NavLink>
            <NavLink to="/metal">{t('nav.metal')}</NavLink>
            <NavLink to="/mebli">{t('nav.mebli')}</NavLink>
            <NavLink to="/hpl">{t('nav.hpl')}</NavLink>

            <div className="trigger">{t('nav.systems')}</div>
            <div className="group">
              <NavLink to="/systems#cctv">{t('systems.cctv')}</NavLink>
              <NavLink to="/systems#acs">{t('systems.acs')}</NavLink>
              <NavLink to="/systems#gates">{t('systems.gates')}</NavLink>
              <NavLink to="/systems#electro">{t('systems.electro')}</NavLink>
              <NavLink to="/systems#water">{t('systems.water')}</NavLink>
              <NavLink to="/systems#k1k2">{t('systems.k1k2')}</NavLink>
              <NavLink to="/systems#vent">{t('systems.vent')}</NavLink>
              <NavLink to="/systems#core">{t('systems.core')}</NavLink>
            </div>

            <NavLink to="/delivery">{t('nav.delivery')}</NavLink>

            <div className="trigger">{t('nav.info')}</div>
            <div className="group">
              <NavLink to="/info/docs">{t('nav.docs')}</NavLink>
              <NavLink to="/info/vacancies">{t('nav.vacancies')}</NavLink>
              <NavLink to="/info/faq">{t('nav.faq')}</NavLink>
              <NavLink to="/articles">{t('nav.articles')}</NavLink>
              <NavLink to="/info/videos">{t('nav.videos')}</NavLink>
            </div>

            <NavLink to="/contacts">{t('nav.contacts')}</NavLink>
          </DrawerList>
        </Drawer>
      </Container>
    </Bar>
  );
}
